import React, { useState } from "react";
import dayjs from "dayjs";
import { CustomDatePicker } from "../../components";

const SampleDatePickerUsage = () => {
  const [date1, setDate1] = useState(null);
  const [date2, setDate2] = useState(dayjs("2024-06-15"));
  const [date3, setDate3] = useState(null);
  const [date4, setDate4] = useState(null);
  const [date5, setDate5] = useState(null);
  const [date6, setDate6] = useState(null);
  const [dateTime1, setDateTime1] = useState(null);

  return (
    <div style={{ padding: "2rem", maxWidth: "900px", margin: "0 auto" }}>
      <h1 style={{ marginBottom: "2rem", color: "#1f2937" }}>
        CustomDatePicker Usage Examples
      </h1>

      {/* Basic Usage */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>Basic Usage</h2>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Select Date"
            name="basicDate"
            value={date1 ? dayjs(date1, "DD/MM/YYYY") : null}
            onChange={(newValue) => {
              if (newValue) {
                setDate1(newValue.format("DD/MM/YYYY"));
              } else {
                setDate1("");
              }
            }}
          />

          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected: {date1 ? date1 : "None"}
          </p>
        </div>
      </section>

      {/* With Default Value */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          With Default Value
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Event Date"
            name="defaultDate"
            defaultValue={dayjs("2024-12-25")}
          />
        </div>
      </section>
      {/* Disabled */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>Disabled</h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Locked Date"
            name="disabledDate"
            defaultValue={dayjs("2024-01-01")}
            disabled
          />
        </div>
      </section>

      {/* Read Only */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>Read Only</h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="View Only Date"
            name="readOnlyDate"
            defaultValue={dayjs("2024-05-20")}
            readOnly
          />
        </div>
      </section>

      {/* Different Sizes */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Different Sizes
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Small"
            name="smallDate"
            size="sm"
            defaultValue={dayjs("2024-02-14")}
          />
          <CustomDatePicker
            label="Medium (Default)"
            name="mediumDate"
            size="md"
            defaultValue={dayjs("2024-06-15")}
          />
          <CustomDatePicker
            label="Large"
            name="largeDate"
            size="lg"
            defaultValue={dayjs("2024-10-31")}
          />
        </div>
      </section>

      {/* Min and Max Date */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Min and Max Date
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Select Date (This Month Only)"
            name="minMaxDate"
            value={date3}
            onChange={(newValue) => setDate3(newValue)}
            minDate={dayjs().startOf("month")}
            maxDate={dayjs().endOf("month")}
            helperText={`Available: ${dayjs()
              .startOf("month")
              .format("MMM D")} - ${dayjs()
              .endOf("month")
              .format("MMM D, YYYY")}`}
          />
          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected: {date3 ? date3.format("DD/MM/YYYY") : "None"}
          </p>
        </div>
      </section>

      {/* Controlled Value */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Controlled Value
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Appointment Date"
            name="controlledDate"
            value={date2}
            onChange={(newValue) => setDate2(newValue)}
          />
          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected: {date2 ? date2.format("DD/MM/YYYY") : "None"}
          </p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              onClick={() => setDate2(dayjs())}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Set Today
            </button>
            <button
              onClick={() => setDate2(null)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#6b7280",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </section>

      {/* Different Date Formats */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Different Date Formats
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1.5rem",
          }}
        >
          <CustomDatePicker
            label="DD/MM/YYYY (Default)"
            name="formatDMY"
            format="DD/MM/YYYY"
            defaultValue={dayjs("2024-06-15")}
          />
          <CustomDatePicker
            label="MM/DD/YYYY"
            name="formatMDY"
            format="MM/DD/YYYY"
            defaultValue={dayjs("2024-06-15")}
          />
          <CustomDatePicker
            label="YYYY-MM-DD"
            name="formatYMD"
            format="YYYY-MM-DD"
            defaultValue={dayjs("2024-06-15")}
          />
          <CustomDatePicker
            label="DD MMM YYYY"
            name="formatLong"
            format="DD MMM YYYY"
            defaultValue={dayjs("2024-06-15")}
          />
          <CustomDatePicker
            label="MMMM D, YYYY"
            name="formatFull"
            format="MMMM D, YYYY"
            defaultValue={dayjs("2024-06-15")}
          />
        </div>
      </section>

      {/* Required Field */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Required Field
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Birth Date"
            name="requiredDate"
            required
            placeholder="Required field"
          />
        </div>
      </section>

      {/* With Error */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>With Error</h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Due Date"
            name="errorDate"
            error="Please select a valid date"
            required
          />
        </div>
      </section>

      {/* With Helper Text */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          With Helper Text
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Start Date"
            name="helperDate"
            helperText="Select your preferred start date"
          />
        </div>
      </section>

      {/* Future Dates Only */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Future Dates Only
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Delivery Date"
            name="futureDate"
            value={date4}
            onChange={(newValue) => setDate4(newValue)}
            minDate={dayjs().add(1, "day")}
            helperText="Select a future date"
          />
        </div>
      </section>

      {/* Past Dates Only */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Past Dates Only
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Date of Birth"
            name="pastDate"
            value={date5}
            onChange={(newValue) => setDate5(newValue)}
            maxDate={dayjs().subtract(1, "day")}
            helperText="Must be a past date"
          />
        </div>
      </section>

      {/* Disable Weekends */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Disable Weekends
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Business Day"
            name="noWeekendsDate"
            value={date6}
            onChange={(newValue) => setDate6(newValue)}
            disableWeekends
            helperText="Weekends are disabled"
          />
          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected: {date6 ? date6.format("dddd, DD/MM/YYYY") : "None"}
          </p>
        </div>
      </section>

      {/* Exclude Specific Dates */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Exclude Specific Dates
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Available Dates"
            name="excludeDates"
            excludeDates={[
              dayjs("2024-12-25"),
              dayjs("2024-12-26"),
              dayjs("2024-01-01"),
            ]}
            helperText="Dec 25, 26 and Jan 1 are excluded (holidays)"
          />
        </div>
      </section>

      {/* Custom Filter Function */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Custom Filter (Only 15th of Each Month)
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Payment Date"
            name="filterDate"
            filterDate={(date) => date.date() === 15}
            helperText="Only 15th of each month is available"
          />
        </div>
      </section>

      {/* Date Time Picker */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Date Time Picker
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "350px",
          }}
        >
          <CustomDatePicker
            label="Event Date & Time"
            name="dateTime"
            value={dateTime1}
            onChange={(newValue) => setDateTime1(newValue)}
            showTimeSelect
          />
          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected:{" "}
            {dateTime1 ? dateTime1.format("DD/MM/YYYY HH:mm") : "None"}
          </p>
        </div>
      </section>

      {/* Non-Clearable */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Non-Clearable
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Permanent Date"
            name="nonClearableDate"
            defaultValue={dayjs("2024-07-04")}
            isClearable={false}
            helperText="This field cannot be cleared"
          />
        </div>
      </section>

      {/* Custom Placeholder */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Custom Placeholder
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomDatePicker
            label="Travel Date"
            name="placeholderDate"
            placeholder="Pick your travel date..."
          />
        </div>
      </section>

      {/* Practical Examples */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Practical Examples
        </h2>

        {/* Date Range Selection */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1.5rem",
            marginBottom: "1.5rem",
            maxWidth: "500px",
          }}
        >
          <h3 style={{ margin: "0 0 1rem 0", color: "#1f2937" }}>
            <span
              className="material-icons"
              style={{ verticalAlign: "middle", marginRight: "0.5rem" }}
            >
              date_range
            </span>
            Book a Trip
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <CustomDatePicker
              label="Check-in Date"
              name="checkIn"
              required
              minDate={dayjs()}
              placeholder="Select check-in"
            />
            <CustomDatePicker
              label="Check-out Date"
              name="checkOut"
              required
              minDate={dayjs().add(1, "day")}
              placeholder="Select check-out"
            />
          </div>
        </div>

        {/* Event Scheduling */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1.5rem",
            marginBottom: "1.5rem",
            maxWidth: "400px",
            backgroundColor: "#f9fafb",
          }}
        >
          <h3 style={{ margin: "0 0 1rem 0", color: "#1f2937" }}>
            <span
              className="material-icons"
              style={{ verticalAlign: "middle", marginRight: "0.5rem" }}
            >
              event
            </span>
            Schedule Meeting
          </h3>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <CustomDatePicker
              label="Meeting Date & Time"
              name="meetingDateTime"
              showTimeSelect
              required
              minDate={dayjs()}
              disableWeekends
              helperText="Select a weekday"
            />
            <button
              style={{
                padding: "0.75rem 1rem",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <span className="material-icons" style={{ fontSize: "1.25rem" }}>
                send
              </span>
              Send Invite
            </button>
          </div>
        </div>

        {/* Birthday Input */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1.5rem",
            maxWidth: "400px",
          }}
        >
          <h3 style={{ margin: "0 0 1rem 0", color: "#1f2937" }}>
            <span
              className="material-icons"
              style={{ verticalAlign: "middle", marginRight: "0.5rem" }}
            >
              cake
            </span>
            User Profile
          </h3>
          <CustomDatePicker
            label="Date of Birth"
            name="dob"
            required
            maxDate={dayjs().subtract(18, "year")}
            format="MMMM D, YYYY"
            helperText="Must be 18 years or older"
          />
        </div>
      </section>

      {/* All States Summary */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          All States Summary
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1.5rem",
          }}
        >
          <CustomDatePicker
            label="Normal"
            name="normalState"
            defaultValue={dayjs("2024-04-15")}
          />
          <CustomDatePicker label="Required" name="requiredState" required />
          <CustomDatePicker
            label="With Error"
            name="errorState"
            error="Invalid date"
            required
          />
          <CustomDatePicker
            label="With Helper"
            name="helperState"
            helperText="Choose carefully"
          />
          <CustomDatePicker
            label="Disabled"
            name="disabledState"
            defaultValue={dayjs("2024-08-20")}
            disabled
          />
          <CustomDatePicker
            label="Read Only"
            name="readOnlyState"
            defaultValue={dayjs("2024-11-11")}
            readOnly
          />
        </div>
      </section>
    </div>
  );
};

export default SampleDatePickerUsage;
